import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import ElectricalFlowDiagram from './ElectricalFlowDiagram';
import VrmEmbed from './VrmEmbed';
import EngineAlarmPanel from './EngineAlarmPanel';
import { heroForRole, subsForRole } from '../../utils/liveBoatUtils';
import { Card, MetricGrid, MetricCard, MetricLabel, MetricValue, MetricSub, Btn } from './liveBoatStyles';

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const PanelHead = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`;

const PanelSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const PanelBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
`;

const SectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.65rem;
`;

const FlowBlock = styled.div`
  margin-bottom: 1.25rem;
`;

const ExpandBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.4rem 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.secondary.light};
  text-align: left;

  &:hover {
    color: ${theme.colors.secondary.main};
  }
`;

const FieldList = styled.div`
  margin-top: 0.35rem;
  padding-top: 0.35rem;
  border-top: 1px solid ${theme.colors.border.secondary};
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.3rem 0;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};

  span:last-child {
    color: ${theme.colors.text.primary};
    font-weight: ${theme.fontWeights.medium};
    text-align: right;
  }
`;

const PanelFoot = styled.div`
  padding: 0.85rem 1.25rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const ExplorerLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.secondary.light};

  &:hover {
    text-decoration: underline;
  }
`;

function RoleMetric({ roleId, role, houseSoc }) {
  const [open, setOpen] = useState(false);
  const subs = subsForRole(roleId, role).slice(0, 2);
  const fields = Object.entries(role.fields).filter(([, f]) => f);
  const hero =
    roleId === 'houseBattery' && houseSoc != null
      ? `${houseSoc.toFixed(1)}%`
      : heroForRole(roleId, role);

  return (
    <MetricCard>
      <MetricLabel>{role.label}</MetricLabel>
      <MetricValue>{hero}</MetricValue>
      {subs.length > 0 ? (
        <MetricSub>
          {subs.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </MetricSub>
      ) : null}
      {fields.length > 2 ? (
        <>
          <ExpandBtn type="button" onClick={() => setOpen(!open)}>
            <span>{open ? 'Hide details' : `${fields.length} readings`}</span>
            {open ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </ExpandBtn>
          {open ? (
            <FieldList>
              {fields.map(([key, field]) => (
                <FieldRow key={key}>
                  <span>{field.reading?.label || key}</span>
                  <span>{field.formatted}</span>
                </FieldRow>
              ))}
            </FieldList>
          ) : null}
        </>
      ) : null}
    </MetricCard>
  );
}

const FlowToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const ZoneDetailPanel = ({ zone, roles, vesselId, houseSoc, vrmEmbedUrl, engineAlarms }) => {
  const [flowView, setFlowView] = useState('vrm');

  if (!zone) return null;

  const showVrm = Boolean(zone.showFlow && vrmEmbedUrl);
  const effectiveFlowView = showVrm ? flowView : 'signalk';

  const visibleRoles = zone.roles
    .map((roleId) => ({ roleId, role: roles[roleId] }))
    .filter(({ role }) => role);

  const explorerHref = zone.explorerCategory
    ? `/vessels/${vesselId}/signalk/explore?category=${encodeURIComponent(zone.explorerCategory)}`
    : `/vessels/${vesselId}/signalk/explore`;

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>{zone.label}</PanelTitle>
        <PanelSub>Live readings from your SignalK stream</PanelSub>
      </PanelHead>

      <PanelBody>
        {zone.id === 'engines' && engineAlarms ? (
          <EngineAlarmPanel rollup={engineAlarms} />
        ) : null}

        {zone.showFlow ? (
          <FlowBlock>
            <SectionLabel>Power flow</SectionLabel>
            {showVrm ? (
              <>
                <FlowToggle>
                  <Btn
                    type="button"
                    data-active={effectiveFlowView === 'vrm'}
                    onClick={() => setFlowView('vrm')}
                  >
                    Victron VRM
                  </Btn>
                  <Btn
                    type="button"
                    data-active={effectiveFlowView === 'signalk'}
                    onClick={() => setFlowView('signalk')}
                  >
                    SignalK
                  </Btn>
                </FlowToggle>
                {effectiveFlowView === 'vrm' ? (
                  <VrmEmbed url={vrmEmbedUrl} />
                ) : (
                  <ElectricalFlowDiagram roles={roles} heroForRole={heroForRole} compact />
                )}
              </>
            ) : (
              <ElectricalFlowDiagram roles={roles} heroForRole={heroForRole} compact />
            )}
          </FlowBlock>
        ) : null}

        <SectionLabel>Readings</SectionLabel>
        <MetricGrid>
          {visibleRoles.map(({ roleId, role }) => (
            <RoleMetric
              key={roleId}
              roleId={roleId}
              role={role}
              houseSoc={houseSoc}
            />
          ))}
        </MetricGrid>
      </PanelBody>

      <PanelFoot>
        <ExplorerLink to={explorerHref}>
          All {zone.explorerCategory || 'SignalK'} paths
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
        </ExplorerLink>
      </PanelFoot>
    </Panel>
  );
};

export default ZoneDetailPanel;
